import { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO_SLIDES } from '../../data/content';
import { HERO } from '../../constants/testIds';

const AUTOPLAY_MS = 6500;

export const HeroSlider = () => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  const total = HERO_SLIDES.length;

  const go = (i) => setIndex(((i % total) + total) % total);
  const next = () => go(index + 1);
  const prev = () => go(index - 1);

  useEffect(() => {
    timerRef.current && clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => go(index + 1), AUTOPLAY_MS);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [index]);

  const slide = HERO_SLIDES[index];

  return (
    <section
      id="top"
      data-testid={HERO.root}
      className="relative h-screen min-h-[760px] w-full overflow-hidden bg-[#18342A]"
    >
      {/* Slides */}
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.image}
          data-testid={HERO.slide(i)}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
            i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          <img
            src={s.image}
            alt={s.title}
            className={`w-full h-full object-cover ${i === index ? 'kenburns' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#18342A]/85 via-[#18342A]/30 to-[#18342A]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#18342A]/60 via-transparent to-transparent" />
        </div>
      ))}

      {/* Grain */}
      <div className="grain-overlay absolute inset-0 z-20 pointer-events-none" />

      {/* Content */}
      <div className="relative z-30 h-full flex flex-col justify-end pb-24 md:pb-28 lg:pb-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px]">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 lg:col-span-8">
              <div
                key={`eyebrow-${index}`}
                className="flex items-center gap-4 mb-6 animate-[fadeUp_0.9s_ease-out_both]"
                style={{ animationName: 'fadeUp' }}
              >
                <span className="w-10 h-px bg-[#C9A458]" />
                <span className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-[#C9A458] font-light">
                  {slide.eyebrow}
                </span>
              </div>
              <h1
                key={`title-${index}`}
                className="font-serif text-[#F5F1E8] text-5xl sm:text-6xl md:text-7xl lg:text-[92px] xl:text-[104px] leading-[0.98] tracking-tight font-light whitespace-pre-line"
                style={{ animation: 'fadeUp 1.1s 0.1s cubic-bezier(0.22,1,0.36,1) both' }}
              >
                {slide.title}
              </h1>
              <p
                key={`sub-${index}`}
                className="mt-8 max-w-xl text-[#F5F1E8]/85 text-base md:text-lg font-light leading-relaxed"
                style={{ animation: 'fadeUp 1.1s 0.25s cubic-bezier(0.22,1,0.36,1) both' }}
              >
                {slide.subtitle}
              </p>

              <div
                className="mt-10 flex flex-wrap items-center gap-4"
                style={{ animation: 'fadeUp 1.1s 0.4s cubic-bezier(0.22,1,0.36,1) both' }}
              >
                <a
                  href="#residences"
                  data-testid={HERO.ctaExplore}
                  className="group inline-flex items-center gap-3 bg-[#C9A458] text-[#18342A] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#F5F1E8] transition-colors duration-500"
                >
                  Explore Residences
                  <ArrowRight size={14} strokeWidth={1.6} className="group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="#contact"
                  data-testid={HERO.ctaVisit}
                  className="group inline-flex items-center gap-3 border border-[#F5F1E8]/40 text-[#F5F1E8] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:border-[#C9A458] hover:text-[#C9A458] transition-colors duration-500"
                >
                  Schedule a Visit
                  <ArrowUpRight size={14} strokeWidth={1.6} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>
            </div>

            {/* Right meta */}
            <div className="hidden lg:flex col-span-4 flex-col items-end gap-6 text-right">
              <div className="text-[#F5F1E8]/70 text-[10px] tracking-[0.4em] uppercase">
                Location
              </div>
              <div
                key={`loc-${index}`}
                className="font-serif text-[#F5F1E8] text-2xl xl:text-3xl leading-tight"
                style={{ animation: 'fadeUp 1.1s 0.3s cubic-bezier(0.22,1,0.36,1) both' }}
              >
                {slide.location}
              </div>
              <div className="hairline w-32 ml-auto" />
              <div className="text-[#C9A458] text-[10px] tracking-[0.4em] uppercase">
                Edition {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 md:mt-16 px-6 md:px-10 lg:px-16 flex items-center justify-between">
          {/* Dots */}
          <div className="flex items-center gap-3">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                type="button"
                data-testid={HERO.dot(i)}
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="group relative h-px w-12 md:w-16 bg-[#F5F1E8]/25 overflow-hidden"
              >
                <span
                  className={`absolute inset-y-0 left-0 bg-[#C9A458] transition-all duration-[1400ms] ease-linear ${
                    i === index ? 'w-full' : 'w-0 group-hover:w-1/3'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Arrows */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid={HERO.prev}
              onClick={prev}
              aria-label="Previous slide"
              className="w-11 h-11 md:w-12 md:h-12 grid place-items-center border border-[#F5F1E8]/30 text-[#F5F1E8] hover:bg-[#C9A458] hover:text-[#18342A] hover:border-[#C9A458] transition-colors"
            >
              <ChevronLeft size={18} strokeWidth={1.4} />
            </button>
            <button
              type="button"
              data-testid={HERO.next}
              onClick={next}
              aria-label="Next slide"
              className="w-11 h-11 md:w-12 md:h-12 grid place-items-center border border-[#F5F1E8]/30 text-[#F5F1E8] hover:bg-[#C9A458] hover:text-[#18342A] hover:border-[#C9A458] transition-colors"
            >
              <ChevronRight size={18} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(28px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default HeroSlider;
